import { Icons, Tooltip } from "@ster/ster-toolkit";
import { memo, useEffect } from "react";
import { useRecoilValue } from "recoil";
import { useDebounce } from "use-debounce";

import { CalculateInput } from "../../api";
import { calculateState } from "../../atoms";
import { Medium } from "../../types";
import sharedStyles from "../shared.module.less";

export interface GrpsInputValue {
  grp?: number;
  spotsPerDayChannel?: number;
}

const formatNumber = (value?: number) =>
  Intl.NumberFormat("nl-NL", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value ?? 0);

const GrpsInput = ({
  medium,
  calculateInput,
  value,
  onChange,
}: {
  medium: Medium;
  calculateInput: CalculateInput;
  value?: GrpsInputValue;
  onChange?: (newValue: GrpsInputValue) => void;
}): JSX.Element => {
  const [debouncedBudget] = useDebounce(calculateInput.budget ?? 0, 500);
  const newValue = useRecoilValue(
    calculateState({
      medium,
      calculateInput: {
        ...calculateInput,
        dateFrom: calculateInput.dateFrom || new Date(),
        dateTo: calculateInput.dateTo || new Date(),
        spotLength: calculateInput.spotLength || 0,
        budget: Number(debouncedBudget),
        calculated: undefined,
      },
    }),
  );

  useEffect(() => {
    if (
      onChange &&
      (value?.grp !== newValue?.grp ||
        value?.spotsPerDayChannel !== newValue?.spotsPerDayChannel)
    ) {
      onChange(newValue);
    }
  }, [newValue, onChange, value]);

  return (
    <>
      <>
        GRP&apos;s{" "}
        <Tooltip
          classNames={{ root: sharedStyles.tooltipWide }}
          title="Afkorting voor gross rating point. Oftewel: je bruto bereik. 1 GRP staat voor één reclamecontact met één procent van de doelgroep. Met andere woorden: 1 GRP is één procent kijk- of luisterdichtheid binnen de doelgroep."
        >
          <span>
            <Icons.QuestionIcon width={16} height={16} />
          </span>
        </Tooltip>
        : {formatNumber(value?.grp)}
      </>
      {value?.spotsPerDayChannel && value?.spotsPerDayChannel > 0 && (
        <>
          <br />
          {`Spot(s) per dag${
            (calculateInput.channels?.length ?? 0) > 1 ? " per zender" : ""
          }`}{" "}
          <Tooltip title="Dit is een indicatie.">
            <span>
              <Icons.QuestionIcon width={16} height={16} />
            </span>
          </Tooltip>
          : {formatNumber(value.spotsPerDayChannel)}
        </>
      )}
    </>
  );
};

export default memo(GrpsInput);
